/**
 * Centralized accepted-file-type categories. Each category exposes an `ACCEPT`
 * string (for the `accept` attribute and drag-drop/raw-input enforcement) and a
 * human-readable `LABEL` (for dropzone hints), so the two never drift apart.
 */

/** Product images & brand assets: raster + vector artwork. SVG is allowed but
 * can't be cropped (product crop is raster-only) — the UI disables crop for it. */
export const DESIGN_ASSET_ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.svg,.pdf,.eps,.ai';
export const DESIGN_ASSET_LABEL = 'JPEG, PNG, WebP, GIF, SVG, PDF, EPS, AI';

/* Brand assets are validated per asset type (Logo/Icon/Image → image, Document
 * → doc, Font → font). See `acceptForBrandType` in the upload modal. */

/** Brand logo/icon/image — the design-artwork set. */
export const BRAND_IMAGE_ACCEPT = DESIGN_ASSET_ACCEPT;
export const BRAND_IMAGE_LABEL = DESIGN_ASSET_LABEL;

/** Brand documents: guides, spec sheets, decks. */
export const BRAND_DOC_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv';
export const BRAND_DOC_LABEL = 'PDF, DOC, PPT, XLS, CSV';

/** Font files. */
export const FONT_ACCEPT = '.ttf,.otf,.woff,.woff2';
export const FONT_LABEL = 'TTF, OTF, WOFF, WOFF2';

/**
 * Logo/avatar images — the selected file goes through the crop step, which
 * bakes a raster PNG. SVG is accepted and croppable (it's rasterized on crop);
 * the original file is still stored separately as `originalLogo`.
 */
export const RASTER_IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.svg';
export const RASTER_IMAGE_LABEL = 'JPEG, PNG, WebP, GIF, SVG';

/** Sample attachments: proof photos, clips, PDFs and fonts. */
export const SAMPLE_MEDIA_ACCEPT = 'image/*,video/*,.pdf,.ttf,.otf,.woff,.woff2';
export const SAMPLE_MEDIA_LABEL = 'Images, videos, PDFs, fonts';

/**
 * Tests a file against an `accept` string (comma-separated extensions like
 * `.png`, wildcard MIME like `image/*`, or exact MIME like `application/pdf`).
 * Mirrors the browser's own accept matching so drag-drop and raw inputs enforce
 * the same rule the file picker shows.
 */
export function matchesAccept(file: File, accept: string): boolean {
	const tokens = accept
		.split(',')
		.map((t) => t.trim().toLowerCase())
		.filter(Boolean);
	if (!tokens.length) return true;

	const name = file.name.toLowerCase();
	const type = file.type.toLowerCase();

	return tokens.some((token) => {
		if (token.startsWith('.')) return name.endsWith(token);
		if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
		return type === token;
	});
}
